import { useProjectId } from '@config/config.hooks';
import {
  Button,
  ComponentType,
  Form,
  FormCheckboxGroup,
  FormFieldMethods,
  IOption,
  LoadingButton,
  ModalBodyExtras,
  useForm,
  useModal,
} from '@gcl/react-core';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { IProjectMasterScheduleTwcOption } from '@masterSchedule/masterSchedule.models';
import { useFetchMasterScheduleTwcOptionsForLoadingRemovalCert } from '@masterSchedule/masterSchedule.queries';
import * as React from 'react';
import { ECreateT4FormKeys } from './permitToLoadRemoveDetailPage.constants';

export interface IUsePermitToLoadRemovalSelectTwcModalProps {
  masterSchedule?: IMasterScheduleDetail;
  mainFormFieldMethods: FormFieldMethods;

  onClickIssueSrp: React.MouseEventHandler<HTMLButtonElement>;
  isEnable: boolean;
}

export const usePermitToLoadRemovalSelectTwcModal = ({
  masterSchedule,
  onClickIssueSrp,
  mainFormFieldMethods,
  isEnable = true,
}: IUsePermitToLoadRemovalSelectTwcModalProps) => {
  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const projectId = useProjectId();

  const { data: twcOptions } = useFetchMasterScheduleTwcOptionsForLoadingRemovalCert(
    {
      projectId,
      masterScheduleId: masterSchedule?.id,
    },
    isEnable,
  );

  const { openPortal, closePortal, renderModal } = useModal();

  const nominatedTwcOptions: IOption<IProjectMasterScheduleTwcOption>[] = React.useMemo(() => {
    return (twcOptions ?? []).map((n) => {
      return { key: n.id, text: n.displayName ?? '', data: n };
    });
  }, [JSON.stringify(twcOptions)]);

  const openPermitToLoadRemovalSelectTwcModal = () => {
    useFormMethods.reset({}, { keepDirty: false });
    openPortal();
  };

  const renderPermitToLoadRemovalSelectTwcModal = (isLoading: boolean, isError: boolean, isSuccess: boolean) => {
    return renderModal({
      large: true,
      title: `Select TWC`,
      bodyExtras: {
        isLoading,
        isError,
        isSuccess,
      },

      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <Form
            useFormMethods={useFormMethods}
            render={(formFieldMethods) => {
              return (
                <>
                  <div>Please choose at least one TWC or TWCM for this T4 to distribute to.</div>
                  <FormCheckboxGroup
                    rules={{
                      validate: (value: any) => {
                        if (value === null) return true;
                        if (value && value.length > 0) return true;
                        return 'At least one should be selected';
                      },
                    }}
                    displayFieldName="text"
                    formFieldMethods={formFieldMethods}
                    checkOptions={nominatedTwcOptions}
                    id={ECreateT4FormKeys.SELECTED_TWCS}
                  />
                </>
              );
            }}
          />
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        return (
          <>
            <Button
              prefixIconName="FileExcel"
              type={ComponentType.Danger}
              onClick={() => {
                if (onHide) {
                  onHide();
                }
              }}
              style={{ margin: '10px' }}
            >
              Cancel
            </Button>
            <LoadingButton
              isLoading={bodyExtras?.isLoading}
              isError={bodyExtras?.isError}
              isSuccess={bodyExtras?.isSuccess}
              onClick={(e) => {
                mainFormFieldMethods.setValue(
                  ECreateT4FormKeys.SELECTED_TWCS,
                  useFormMethods.getValues(ECreateT4FormKeys.SELECTED_TWCS),
                );
                onClickIssueSrp(e);
              }}
              timeToResetSuccessState={3000}
              timeToResetErrorState={5000}
              type={ComponentType.Success}
            >
              Confirm to issue to TWC
            </LoadingButton>
          </>
        );
      },
    });
  };
  return {
    openPortal: openPermitToLoadRemovalSelectTwcModal,
    closePortal,
    renderModal: renderPermitToLoadRemovalSelectTwcModal,
  };
};
